import { forwardRef } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';

type ChatComposerProps = {
  input: string;
  isTyping: boolean;
  /** Locks the composer once the conversation reaches its terminal message. */
  disabled?: boolean;
  onInputChange: (value: string) => void;
  onSend: () => void;
};

export const ChatComposer = forwardRef<HTMLTextAreaElement, ChatComposerProps>(
  ({ input, isTyping, disabled = false, onInputChange, onSend }, textareaRef) => {
    return (
      <div className="border-t border-border-subtle p-3">
        <div className="flex items-center gap-2 rounded-3xl bg-card-subtle p-2">
          <Textarea
            ref={textareaRef}
            placeholder="Type your response…"
            value={input}
            disabled={disabled}
            onChange={(event) => onInputChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
            className="min-h-[40px] max-h-[120px] flex-1 resize-none border-0 bg-transparent p-2 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            rows={1}
          />
          <Button
            onClick={onSend}
            disabled={!input.trim() || isTyping || disabled}
            className="h-9 w-9 flex-shrink-0 rounded-full bg-sage p-0 transition-colors duration-150 ease-out hover:bg-[hsl(169_95%_18%)] disabled:bg-sage/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            aria-label="Send"
          >
            <Send className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    );
  },
);

ChatComposer.displayName = 'ChatComposer';
